import React, { useState, useEffect } from 'react';

import { Tooltip, ActionIcon } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { AxiosResponse } from 'axios';
import { ExportToCsv, Options } from 'export-to-csv';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { UseInfiniteQueryResult } from 'react-query';

export type List<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<T>;
};

interface DownloadCSVProps<T> {
  useList: (params: any) => UseInfiniteQueryResult<AxiosResponse<List<T>>>;
  dataToRow: (data: T) => { [key: string]: any };
  csvOptions?: Options;
  enabled?: boolean;
  hookOptions?: { [key: string]: any };
  className?: string;
  limit?: string;
  reportType: string;
  startDate?: string | null;
  endDate?: string | null;
}
const DownloadCSV = <T,>({
  useList,
  dataToRow,
  csvOptions,
  enabled = true,
  hookOptions,
  limit = '100',
  className,
  reportType,
  startDate,
  endDate,
}: DownloadCSVProps<T>): JSX.Element => {
  const csvExporter = new ExportToCsv(csvOptions);
  const tracking = useTracking();
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const { data, isSuccess, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useList({
    enabled: buttonClicked && enabled,
    limit,
    csvExport: 'csv-export',
    ...hookOptions,
  });

  useEffect(() => {
    if (buttonClicked && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, buttonClicked]);

  useEffect(() => {
    if (buttonClicked && isSuccess && !hasNextPage) {
      const exportData = data.pages.map(page => page.data.results).flat();
      if (exportData.length) {
        csvExporter.generateCsv(exportData.map(athlete => dataToRow(athlete)));
        tracking.trackReportGenerated(reportType, startDate ?? null, endDate ?? null);
      }
      setButtonClicked(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, hasNextPage, buttonClicked, data?.pages, reportType, startDate, endDate, tracking]);

  return (
    <Tooltip label='Export data as CSV' className={className}>
      <ActionIcon
        onClick={() => setButtonClicked(true)}
        loading={buttonClicked && (isLoading || hasNextPage)}
        data-cy='downloadCsvButton'
      >
        <IconPrinter />
      </ActionIcon>
    </Tooltip>
  );
};

export default DownloadCSV;
